import React, { useState, useEffect, useCallback } from 'react';
import { folderAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import './AiOrganizeModal.css';

interface AiSuggestion {
  folder_name: string;
  folder_color: string | null;
  is_new: boolean;
  report_ids: number[];
}

interface ReportInfo {
  id: number;
  title: string;
}

interface AiOrganizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplied: () => void;
  reports: ReportInfo[];
}

const AiOrganizeModal: React.FC<AiOrganizeModalProps> = ({ isOpen, onClose, onApplied, reports }) => {
  const { t } = useTranslation('reports');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState<AiSuggestion[]>([]);
  const [checkedReports, setCheckedReports] = useState<Record<number, Set<number>>>({});

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await folderAPI.aiOrganize();
      const data = response.data;
      setSuggestions(data.suggestions || []);

      // Initialize checked state: all reports checked by default
      const initial: Record<number, Set<number>> = {};
      (data.suggestions || []).forEach((s: AiSuggestion, idx: number) => {
        initial[idx] = new Set(s.report_ids);
      });
      setCheckedReports(initial);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('ai_organize_error'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchSuggestions();
    } else {
      setSuggestions([]);
      setCheckedReports({});
      setError('');
    }
  }, [isOpen, fetchSuggestions]);

  const toggleReport = (groupIdx: number, reportId: number) => {
    setCheckedReports(prev => {
      const updated = { ...prev };
      const set = new Set(updated[groupIdx] || []);
      if (set.has(reportId)) {
        set.delete(reportId);
      } else {
        set.add(reportId);
      }
      updated[groupIdx] = set;
      return updated;
    });
  };

  const removeGroup = (idx: number) => {
    setSuggestions(prev => prev.filter((_, i) => i !== idx));
    setCheckedReports(prev => {
      const updated: Record<number, Set<number>> = {};
      let newIdx = 0;
      Object.keys(prev).forEach(key => {
        const k = Number(key);
        if (k !== idx) {
          updated[newIdx] = prev[k];
          newIdx++;
        }
      });
      return updated;
    });
  };

  const updateFolderName = (idx: number, name: string) => {
    setSuggestions(prev => prev.map((s, i) => i === idx ? { ...s, folder_name: name } : s));
  };

  const handleApply = async () => {
    setApplying(true);
    setError('');
    try {
      const filtered = suggestions
        .map((s, idx) => ({
          ...s,
          report_ids: Array.from(checkedReports[idx] || []),
        }))
        .filter(s => s.report_ids.length > 0);

      await folderAPI.aiOrganizeApply({ suggestions: filtered });
      onApplied();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('ai_organize_apply_error'));
    } finally {
      setApplying(false);
    }
  };

  const getReportTitle = (reportId: number): string => {
    const report = reports.find(r => r.id === reportId);
    return report?.title || t('report_title_fallback', { id: reportId });
  };

  const hasAnySuggestions = suggestions.length > 0;
  const hasAnyChecked = Object.values(checkedReports).some(set => set.size > 0);

  if (!isOpen) return null;

  return (
    <div className="ai-modal-overlay" onClick={onClose}>
      <div className="ai-modal" onClick={e => e.stopPropagation()}>
        <div className="ai-modal-header">
          <h2>
            <i className="fas fa-magic"></i>
            {t('ai_organize_title')}
          </h2>
          <button className="ai-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="ai-modal-body">
          {loading ? (
            <div className="ai-loading">
              <div className="ai-loading-spinner" />
              <p>{t('ai_organize_loading')}</p>
            </div>
          ) : error && !hasAnySuggestions ? (
            <div className="ai-error">{error}</div>
          ) : !hasAnySuggestions ? (
            <div className="ai-empty">
              <i className="fas fa-check-circle"></i>
              <p>{t('ai_organize_empty')}</p>
            </div>
          ) : (
            <>
              {error && <div className="ai-error" style={{ marginBottom: 16 }}>{error}</div>}
              {suggestions.map((suggestion, idx) => (
                <div key={idx} className="ai-suggestion-group">
                  <div className="ai-suggestion-header">
                    <div className="ai-folder-name-wrapper">
                      <span
                        className="ai-folder-color-dot"
                        style={{ background: suggestion.folder_color || '#3498db' }}
                      />
                      <input
                        className="ai-folder-name-input"
                        value={suggestion.folder_name}
                        onChange={e => updateFolderName(idx, e.target.value)}
                      />
                      {suggestion.is_new && <span className="ai-new-badge">{t('ai_organize_new_badge')}</span>}
                    </div>
                    <button className="ai-remove-group" onClick={() => removeGroup(idx)} title={t('ai_organize_remove_title')}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                  <div className="ai-report-list">
                    {suggestion.report_ids.map(reportId => (
                      <div key={reportId} className="ai-report-item">
                        <input
                          type="checkbox"
                          id={`ai-report-${idx}-${reportId}`}
                          checked={checkedReports[idx]?.has(reportId) ?? false}
                          onChange={() => toggleReport(idx, reportId)}
                        />
                        <label htmlFor={`ai-report-${idx}-${reportId}`}>
                          {getReportTitle(reportId)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="ai-modal-footer">
          <button className="ai-btn ai-btn-secondary" onClick={onClose}>
            {t('common:cancel', { ns: 'common' })}
          </button>
          {hasAnySuggestions && (
            <button
              className="ai-btn ai-btn-primary"
              onClick={handleApply}
              disabled={applying || !hasAnyChecked}
            >
              {applying ? t('ai_organize_applying') : t('ai_organize_apply')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiOrganizeModal;
